/**
 * Chip primitive — TASK 7.1.
 *
 * The mock's toggleable genre filter on /search (L356):
 *   font-size:12px;font-weight:700;border:2px solid #0D0916;border-radius:999px;
 *   padding:8px 14px;min-height:36px;background:{{ ch.bg }};color:{{ ch.col }}
 * with `aria-pressed` carrying the selected state and the active fill being
 * #8E5CFF on #171221 text (mock L1050).
 *
 * Two things the extraction adds, both required and neither visual:
 *   - `aria-pressed` drives the active fill through the `.sm-chip[aria-pressed]`
 *     selector, so the state lives in ONE place (the a11y attribute) instead of
 *     a colour string. State is therefore never conveyed by colour alone.
 *   - the mock's `min-height:36px` is below the 44px floor, so `.sm-chip`
 *     restores a 44px HIT area with symmetric block padding plus a matching
 *     negative block margin: the painted pill is still 36px tall and the
 *     surrounding layout is unchanged, but the touch target complies
 *     (TASK 7.1 Step 7 / W22).
 *
 * The same primitive backs the two tab strips (browse L218/219, queue
 * L578/579) and the stats period toggles (L541/542) via `Tab()` below —
 * they are the same pill chassis at display type.
 *
 * @module
 */

import { classes, el } from './dom.js';

/**
 * @param {{
 *   label: string,
 *   pressed?: boolean,
 *   onClick?: (event: Event) => void,
 *   testId?: string,
 *   ariaLabel?: string,
 * }} options
 * @returns {HTMLButtonElement}
 */
export function Chip(options) {
  const { label, pressed = false, onClick, testId, ariaLabel } = options;
  /** @type {Record<string, string|number|boolean|null|undefined>} */
  const attrs = { type: 'button', 'aria-pressed': String(pressed) };
  if (ariaLabel !== undefined) attrs['aria-label'] = ariaLabel;

  return /** @type {HTMLButtonElement} */ (
    el('button', {
      className: 'sm-chip',
      text: label,
      testId,
      attrs,
      ...(onClick ? { on: { click: onClick } } : {}),
    })
  );
}

/**
 * Tab / segmented control — mock L218-219 (BROWSE artists|genres, 18px),
 * L578-579 (QUEUE queue|history, 17px) and L541-542 (STATS all|30d, 16px,
 * `aria-pressed` instead of `aria-selected`).
 *
 * `accent` selects which fill the SELECTED state paints, matching the mock:
 * artists -> pink, genres -> purple, queue -> pink, history -> yellow,
 * all-time -> teal, last-30-days -> yellow.
 *
 * @param {{
 *   label: string,
 *   accent: 'pink'|'purple'|'yellow'|'teal',
 *   selected?: boolean,
 *   href?: string,
 *   onClick?: (event: Event) => void,
 *   size?: 'lg'|'md'|'period',
 *   testId?: string,
 * }} options
 * @returns {HTMLElement}
 */
export function Tab(options) {
  const { label, accent, selected = false, href, onClick, size = 'lg', testId } = options;

  const SIZE_CLASS = { lg: undefined, md: 'sm-tab--17', period: 'sm-tab--period' };
  if (!(size in SIZE_CLASS)) throw new Error(`Tab: unknown size "${String(size)}"`);

  const isLink = typeof href === 'string';
  // Link tabs use aria-selected (role=tab); the stats toggles are buttons and
  // use aria-pressed — the CSS selectors mirror exactly that split.
  const ACCENT_CLASS = isLink
    ? { pink: 'sm-tab--pink', purple: 'sm-tab--purple', yellow: 'sm-tab--yellow' }
    : { yellow: 'sm-tab--period-yellow', teal: 'sm-tab--period-teal' };
  const accentClass = ACCENT_CLASS[accent];
  if (accentClass === undefined) {
    throw new Error(
      `Tab: accent "${String(accent)}" is not one of the mock's ` +
        `${isLink ? 'link' : 'button'} tab treatments (${Object.keys(ACCENT_CLASS).join('|')})`,
    );
  }

  /** @type {Record<string, string|number|boolean|null|undefined>} */
  const attrs = isLink
    ? { role: 'tab', href, 'aria-selected': String(selected) }
    : { type: 'button', 'aria-pressed': String(selected) };

  return el(isLink ? 'a' : 'button', {
    className: classes('sm-tab', SIZE_CLASS[size], accentClass),
    text: label,
    testId,
    attrs,
    ...(onClick ? { on: { click: onClick } } : {}),
  });
}
